import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, X } from "lucide-react";

import {
  addExternalPerson,
  createExternalOrganization,
  linkExternalOrganization,
  listExternalOrganizationLinks,
  listExternalOrganizations,
  queueInviteRequest,
  unlinkExternalOrganization,
  type ExternalOrganization,
  type ExternalOrganizationLink,
} from "@/api/external_organizations";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentUser } from "@/context/UserProvider";
import { useToast } from "@/hooks/useToast";
import { userIsOutsideUser } from "@/lib/grantSuiteAccess";
import { invalidateGrantOpportunityEvents } from "@/lib/grantSuiteQueryKeys";

type ExternalOrganizationsCardProps = {
  municipalityId: number;
  opportunityId: number;
};

// Outside support organizations (e.g. Tarrant County) linked to this
// opportunity. People here are email records, never team members; queueing an
// invite stores a pending request that Ordinal staff send or reject later.
export function ExternalOrganizationsCard({
  municipalityId,
  opportunityId,
}: ExternalOrganizationsCardProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  // Outside collaborators see which organizations are on the opportunity
  // (their own among them) but manage nothing: no link/unlink/create, and no
  // people roster — the organizations read returns them nothing anyway
  // (ENG-1877).
  const outsideUser = userIsOutsideUser(currentUser);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [newOrgName, setNewOrgName] = useState("");

  const { data: organizationsData, isPending: organizationsPending } = useQuery({
    queryKey: ["external_organizations", municipalityId] as const,
    queryFn: () => listExternalOrganizations(municipalityId),
    enabled: !outsideUser,
  });
  const organizations: ExternalOrganization[] = Array.isArray(organizationsData)
    ? organizationsData
    : [];

  const { data: linksData } = useQuery({
    queryKey: ["external_organization_links", municipalityId, opportunityId] as const,
    queryFn: () => listExternalOrganizationLinks(municipalityId, opportunityId),
  });
  const links: ExternalOrganizationLink[] = Array.isArray(linksData) ? linksData : [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["external_organizations", municipalityId] });
    queryClient.invalidateQueries({
      queryKey: ["external_organization_links", municipalityId, opportunityId],
    });
    // Every write here records an activity event, and adding a person or
    // queueing an invite records it on every opportunity the organization is
    // linked to — not just this one — so sweep the team's feeds.
    invalidateGrantOpportunityEvents(queryClient, municipalityId);
  };

  const onError = (error: Error) => {
    toast({ title: error.message, variant: "destructive" });
  };

  const linkMutation = useMutation({
    mutationFn: (organizationId: number) =>
      linkExternalOrganization(municipalityId, opportunityId, organizationId),
    onSuccess: () => {
      setSelectedOrgId("");
      invalidate();
    },
    onError,
  });

  const createAndLinkMutation = useMutation({
    mutationFn: async (name: string) => {
      const organization = await createExternalOrganization(municipalityId, name);
      await linkExternalOrganization(municipalityId, opportunityId, organization.id);
    },
    onSuccess: () => setNewOrgName(""),
    onError,
    // Invalidate even on failure: when create succeeded but link failed, the
    // organization exists server-side and must show up in the link list —
    // otherwise retrying Create just 422s on the duplicate name.
    onSettled: invalidate,
  });

  const unlinkMutation = useMutation({
    mutationFn: (linkId: number) =>
      unlinkExternalOrganization(municipalityId, opportunityId, linkId),
    onSuccess: invalidate,
    onError,
  });

  // Keyed off the links payload alone (it carries the name), so a failed or
  // stale organizations query can never blank out linked rows — only the
  // people details degrade.
  const linkedOrganizations = useMemo(
    () =>
      links.map((link) => ({
        link,
        organization:
          organizations.find((org) => org.id === link.external_organization_id) ?? null,
      })),
    [links, organizations],
  );

  const unlinkedOrganizations = useMemo(
    () =>
      organizations.filter(
        (org) => !links.some((link) => link.external_organization_id === org.id),
      ),
    [organizations, links],
  );

  if (outsideUser) {
    return (
      <Card data-testid="external-organizations-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">External organizations</CardTitle>
          <CardDescription>
            Outside organizations supporting this opportunity.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {links.length === 0 ? (
            <p className="text-sm text-muted-foreground">No external organizations linked.</p>
          ) : (
            links.map((link) => (
              <p
                key={link.id}
                className="flex items-center gap-1.5 text-sm font-medium"
                data-testid="linked-external-organization"
              >
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                {link.name}
              </p>
            ))
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="external-organizations-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">External organizations</CardTitle>
        <CardDescription>
          Outside organizations supporting this opportunity. Their people are not team members;
          queued invites are sent by Ordinal staff.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {linkedOrganizations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No external organizations linked yet.</p>
        ) : (
          linkedOrganizations.map(({ link, organization }) => (
            <LinkedOrganization
              key={link.id}
              municipalityId={municipalityId}
              name={link.name}
              organization={organization}
              organizationsPending={organizationsPending}
              onUnlink={() => unlinkMutation.mutate(link.id)}
              unlinking={unlinkMutation.isPending && unlinkMutation.variables === link.id}
              onChanged={invalidate}
              onError={onError}
            />
          ))
        )}

        <div className="space-y-2 border-t pt-3">
          {unlinkedOrganizations.length > 0 ? (
            <div className="flex gap-2">
              <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
                <SelectTrigger aria-label="Link an external organization" className="h-8 flex-1">
                  <SelectValue placeholder="Link an organization…" />
                </SelectTrigger>
                <SelectContent>
                  {unlinkedOrganizations.map((org) => (
                    <SelectItem key={org.id} value={String(org.id)}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                disabled={!selectedOrgId || linkMutation.isPending}
                onClick={() => linkMutation.mutate(Number(selectedOrgId))}
              >
                Link
              </Button>
            </div>
          ) : null}

          <div className="flex gap-2">
            <Input
              className="h-8"
              placeholder="New organization name…"
              value={newOrgName}
              onChange={(event) => setNewOrgName(event.target.value)}
              aria-label="New external organization name"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={!newOrgName.trim() || createAndLinkMutation.isPending}
              onClick={() => createAndLinkMutation.mutate(newOrgName.trim())}
            >
              <Plus className="mr-1 h-3.5 w-3.5" />
              Create
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type LinkedOrganizationProps = {
  municipalityId: number;
  // Display name from the link payload, so the row renders even when the
  // organizations query has failed or not settled.
  name: string;
  organization: ExternalOrganization | null;
  // Distinguishes "organizations still loading" from "settled without this
  // org", so a normal load never flashes the unavailable message.
  organizationsPending: boolean;
  onUnlink: () => void;
  unlinking: boolean;
  onChanged: () => void;
  onError: (error: Error) => void;
};

function LinkedOrganization({
  municipalityId,
  name,
  organization,
  organizationsPending,
  onUnlink,
  unlinking,
  onChanged,
  onError,
}: LinkedOrganizationProps) {
  const [email, setEmail] = useState("");
  const [personName, setPersonName] = useState("");

  const addPersonMutation = useMutation({
    mutationFn: (organizationId: number) =>
      addExternalPerson(municipalityId, organizationId, {
        email: email.trim(),
        name: personName.trim() || undefined,
      }),
    onSuccess: () => {
      setEmail("");
      setPersonName("");
      onChanged();
    },
    onError,
  });

  const inviteMutation = useMutation({
    mutationFn: ({ organizationId, personId }: { organizationId: number; personId: number }) =>
      queueInviteRequest(municipalityId, organizationId, personId),
    onSuccess: onChanged,
    onError,
  });

  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid="linked-external-organization">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-medium">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          {name}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          aria-label={`Unlink ${name}`}
          disabled={unlinking}
          onClick={onUnlink}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {organization === null && organizationsPending ? (
        <p className="text-xs text-muted-foreground">Loading people…</p>
      ) : organization === null ? (
        <p className="text-xs text-muted-foreground">People are unavailable right now.</p>
      ) : (
        <>
          {organization.people.length > 0 ? (
            <ul className="space-y-1.5">
              {organization.people.map((person) => (
                <li key={person.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="min-w-0 truncate">
                    {person.name ? `${person.name} · ` : ""}
                    <span className="text-muted-foreground">{person.email}</span>
                  </span>
                  {person.invite_pending ? (
                    <Badge variant="secondary" className="shrink-0 font-normal">
                      Invite queued
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 shrink-0 px-2 text-xs"
                      disabled={inviteMutation.isPending && inviteMutation.variables?.personId === person.id}
                      onClick={() =>
                        inviteMutation.mutate({ organizationId: organization.id, personId: person.id })
                      }
                    >
                      Queue invite
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">No people added yet.</p>
          )}

          <form
            className="flex flex-wrap gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (email.trim() && !addPersonMutation.isPending) {
                addPersonMutation.mutate(organization.id);
              }
            }}
          >
            <Input
              type="email"
              className="h-8 min-w-0 flex-1"
              placeholder="person@example.gov"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-label={`Add person email for ${name}`}
            />
            <Input
              className="h-8 w-28"
              placeholder="Name"
              value={personName}
              onChange={(event) => setPersonName(event.target.value)}
              aria-label={`Add person name for ${name}`}
            />
            <Button
              type="submit"
              size="sm"
              variant="outline"
              disabled={!email.trim() || addPersonMutation.isPending}
            >
              Add
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
